/**
 * socialService — REST client for the social graph.
 *
 * Speaks `/api/connections/*` (Phase B). Block/unblock/report stay on the
 * existing `safetyService` (`/api/safety/*`) — this client never re-implements
 * blocking; it only reads the graph state the block store feeds.
 *
 * Follows the messaging-spine convention: throw on `res.error` so callers
 * (contexts/hooks) get a rejected promise rather than a null they must
 * null-check.
 */

import { apiService } from './apiService';
import type {
  Connection,
  ConnectionRequest,
  ConnectionState,
  CreateConnectionRequest,
} from '../../types/social';

const BASE = '/api/connections';

/** Create a pending outbound connection request. Idempotent server-side. */
export async function sendConnectionRequest(
  targetProfileId: string
): Promise<ConnectionRequest | null> {
  const body: CreateConnectionRequest = { targetProfileId };
  const res = await apiService.post<ConnectionRequest>(`${BASE}/requests`, body);
  if (res.error) throw new Error(res.error);
  // 200 with no body is the "silently accepted / target has blocked me" case
  // (AC-06.3) — surface it as null rather than an error so the UI doesn't leak.
  return res.data ?? null;
}

/** Pending inbound requests awaiting the caller's accept/decline. */
export async function getInboundRequests(): Promise<ConnectionRequest[]> {
  const res = await apiService.get<ConnectionRequest[]>(`${BASE}/requests/inbound`);
  if (res.error) throw new Error(res.error);
  return res.data ?? [];
}

/** Pending outbound requests the caller has sent (drives the card's "Requested" state). */
export async function getOutboundRequests(): Promise<ConnectionRequest[]> {
  const res = await apiService.get<ConnectionRequest[]>(`${BASE}/requests/outbound`);
  if (res.error) throw new Error(res.error);
  return res.data ?? [];
}

/** Accept a pending inbound request → creates the connection edge. */
export async function acceptRequest(requestId: string): Promise<Connection | null> {
  const res = await apiService.post<Connection>(`${BASE}/requests/${requestId}/accept`);
  if (res.error) throw new Error(res.error);
  return res.data ?? null;
}

/** Decline a pending inbound request. No connection is created. */
export async function declineRequest(requestId: string): Promise<boolean> {
  const res = await apiService.post<{ success: boolean }>(
    `${BASE}/requests/${requestId}/decline`
  );
  if (res.error) throw new Error(res.error);
  return res.data?.success ?? true;
}

/** Cancel the caller's own pending outbound request. */
export async function cancelRequest(requestId: string): Promise<boolean> {
  const res = await apiService.delete<{ success: boolean }>(`${BASE}/requests/${requestId}`);
  if (res.error) throw new Error(res.error);
  return res.data?.success ?? true;
}

/** Accepted connections + mute flag + presence stub. */
export async function getConnections(): Promise<Connection[]> {
  const res = await apiService.get<Connection[]>(BASE);
  if (res.error) throw new Error(res.error);
  return res.data ?? [];
}

/** Fast single-profile ConnectionState for card/gate rendering. */
export async function getConnectionState(profileId: string): Promise<ConnectionState> {
  const res = await apiService.get<{ state: ConnectionState }>(`${BASE}/state/${profileId}`);
  if (res.error) throw new Error(res.error);
  return res.data?.state ?? 'none';
}

/** Batch state lookup for a roster (in-game connection marks). */
export async function getConnectionStates(
  profileIds: string[]
): Promise<Record<string, ConnectionState>> {
  if (profileIds.length === 0) return {};
  const res = await apiService.post<Record<string, ConnectionState>>(`${BASE}/state/batch`, {
    profileIds,
  });
  if (res.error) throw new Error(res.error);
  return res.data ?? {};
}

/** Graph-local mute — the connection stays 'accepted'. */
export async function muteConnection(connectionId: string): Promise<boolean> {
  const res = await apiService.post<{ success: boolean }>(`${BASE}/${connectionId}/mute`);
  if (res.error) throw new Error(res.error);
  return res.data?.success ?? true;
}

export async function unmuteConnection(connectionId: string): Promise<boolean> {
  const res = await apiService.post<{ success: boolean }>(`${BASE}/${connectionId}/unmute`);
  if (res.error) throw new Error(res.error);
  return res.data?.success ?? true;
}

export const socialService = {
  sendConnectionRequest,
  getInboundRequests,
  getOutboundRequests,
  acceptRequest,
  declineRequest,
  cancelRequest,
  getConnections,
  getConnectionState,
  getConnectionStates,
  muteConnection,
  unmuteConnection,
};

export default socialService;
